import { useState } from 'react';

// Single-photo tagging UI for FullScreenView's actionArea.
// Renders four pieces:
//  - a small fixed banner above the image showing the photo's current tags
//  - a vertical "Recent" tag column to the left of the image
//  - a "Clear Tags" bubble to the right of the image (only when there's something to clear)
//  - a bottom panel with the search/edit controls
//
// NOTE: render this with key={photo.id} from the parent so the component
// remounts (and re-initializes its state from `photo.credits`) whenever
// the active photo changes — avoids resetting state via a setState-in-effect.
export default function FullScreenTagPanel({
    photo,
    artists,
    tagHistory,
    onSaveTags,      // (photoId, artistIds) => Promise<boolean>
    onMarkAsGroup,   // (photoId, opts?: { confirmed?: boolean }) => Promise<boolean>
    onSaveNamedTag,  // (name, artistIds) => Promise<boolean>
    onDeleteTag,     // (tagId) => void
}) {
    const [selectedArtists, setSelectedArtists] = useState(() =>
        (photo.credits ?? [])
            .filter(c => c.userId)
            .map(c => String(c.userId.id ?? c.userId))
    );
    const [artistQuery, setArtistQuery] = useState('');
    const [tagName, setTagName] = useState('');
    const [saving, setSaving] = useState(false);
    const [savingTag, setSavingTag] = useState(false);
    const [saveStatus, setSaveStatus] = useState('');

    const creditIds = (photo.credits ?? [])
        .filter(c => c.userId)
        .map(c => String(c.userId.id ?? c.userId))
        .sort()
        .join(',');

    const toggleArtist = (artistId, checked) => {
        setSelectedArtists(prev =>
            checked ? [...prev, String(artistId)] : prev.filter(id => id !== String(artistId))
        );
        if (checked) setArtistQuery('');
    };

    const filteredArtists = artistQuery
        ? artists.filter(a =>
            `${a.fName} ${a.lName}`.toLowerCase().includes(artistQuery.toLowerCase())
          )
        : [];
    const unselectedMatches = filteredArtists.filter(a => !selectedArtists.includes(String(a.id)));

    const hasChanges = (() => {
        const current = creditIds ? creditIds.split(',') : [];
        const next = [...selectedArtists].sort();
        return current.length !== next.length || current.some((id, i) => id !== next[i]);
    })();

    const handleSave = async () => {
        setSaving(true);
        const ok = await onSaveTags(photo.id, selectedArtists);
        setSaving(false);
        if (ok) {
            setSaveStatus('saved');
            setTimeout(() => setSaveStatus(''), 2000);
        }
    };

    const handleClear = async () => {
        if (!confirm('Remove all tags from this photo?')) return;
        setSaving(true);
        const ok = await onSaveTags(photo.id, []);
        setSaving(false);
        if (ok) setSelectedArtists([]);
    };

    const handleMarkGroup = async () => {
        setSaving(true);
        // Only ask for confirmation if there are individual tags that would be lost.
        const hasIndividualTags = (photo.credits ?? []).some(c => c.userId);
        const ok = await onMarkAsGroup(photo.id, { confirmed: !hasIndividualTags });
        setSaving(false);
        if (ok) setSelectedArtists([]);
    };

    const handleSaveTag = async () => {
        if (!tagName.trim() || selectedArtists.length === 0) return;
        setSavingTag(true);
        const ok = await onSaveNamedTag(tagName.trim(), selectedArtists);
        setSavingTag(false);
        if (ok) setTagName('');
    };

    let currentLabel;
    if (photo.isGroupPhoto) {
        currentLabel = 'Group photo';
    } else {
        const names = (photo.credits ?? [])
            .filter(c => c.userId)
            .map(c => `${c.userId.fName} ${c.userId.lName}`);
        currentLabel = names.length > 0 ? names.join(', ') : 'Untagged';
    }

    return (
        <>
            {/* Fixed banner above the image — shows the photo's saved tag state */}
            <div className='fs-tag-current-bar' onClick={e => e.stopPropagation()}>
                <span className='fs-tag-current-label'>Currently tagged:</span>
                <span className={`fs-tag-current-value${photo.isGroupPhoto ? ' group' : ''}`}>
                    {currentLabel}
                </span>
                {saveStatus === 'saved' && <span className='fs-tag-saved'>✓ Saved</span>}
            </div>

            {/* Recent tags — vertical column to the left of the image */}
            {tagHistory.length > 0 && (
                <div className='fs-tag-history-sidebar' onClick={e => e.stopPropagation()}>
                    <span className='tag-history-label'>Recent</span>
                    {tagHistory.map(tag => {
                        const tagArtistIds = tag.artistIds.map(a => String(a.id));
                        const isSingle = tagArtistIds.length === 1;
                        const isActive = isSingle
                            ? selectedArtists.includes(tagArtistIds[0])
                            : tagArtistIds.length === selectedArtists.length &&
                              tagArtistIds.every(id => selectedArtists.includes(id));

                        const handleClick = () => {
                            if (isSingle) {
                                // Stack: toggle this one artist in/out of the selection.
                                const id = tagArtistIds[0];
                                setSelectedArtists(prev =>
                                    prev.includes(id) ? prev.filter(a => a !== id) : [...prev, id]
                                );
                            } else if (isActive) {
                                setSelectedArtists([]);
                            } else {
                                setSelectedArtists(tagArtistIds);
                            }
                            setArtistQuery('');
                        };

                        return (
                            <span key={tag.id} className={`tag-history-chip${isActive ? ' active' : ''}`}>
                                <button className='tag-history-btn' onClick={handleClick}>
                                    {tag.name}
                                </button>
                                <button
                                    className='tag-history-delete'
                                    title='Remove this tag'
                                    onClick={() => onDeleteTag(tag.id)}
                                >
                                    ×
                                </button>
                            </span>
                        );
                    })}
                </div>
            )}

            {/* Clear Tags — bubble to the right of the image, only when there's something to clear */}
            {(photo.credits?.length > 0 || photo.isGroupPhoto) && (
                <button
                    className='tag-clear-btn fs-tag-clear-bubble'
                    onClick={e => { e.stopPropagation(); handleClear(); }}
                    disabled={saving}
                >
                    Clear Tags
                </button>
            )}

            {/* Bottom panel — editing controls, fixed-height regardless of tag history */}
            <div className='fs-tag-panel' onClick={e => e.stopPropagation()}>
                <div className='fs-tag-main-row'>
                    <input
                        type='search'
                        className='artist-search fs-artist-search'
                        placeholder='Search artists…'
                        value={artistQuery}
                        onChange={e => setArtistQuery(e.target.value)}
                    />
                    {artistQuery && (
                        filteredArtists.length === 0
                            ? <span className='artist-no-results'>No artists found</span>
                            : unselectedMatches.map(a => (
                                <label key={a.id} className='artist-check-row'>
                                    <input
                                        type='checkbox'
                                        checked={false}
                                        onChange={e => toggleArtist(a.id, e.target.checked)}
                                    />
                                    {a.fName} {a.lName}
                                </label>
                            ))
                    )}
                    {selectedArtists.map(id => {
                        const artist = artists.find(a => String(a.id) === id);
                        return artist ? (
                            <label key={id} className='artist-check-row selected'>
                                <input
                                    type='checkbox'
                                    checked={true}
                                    onChange={() => toggleArtist(id, false)}
                                />
                                {artist.fName} {artist.lName}
                            </label>
                        ) : null;
                    })}

                    <div className='fs-tag-main-actions'>
                        <button
                            className='tag-save-btn tag-group-btn'
                            onClick={handleMarkGroup}
                            disabled={saving || photo.isGroupPhoto}
                        >
                            Tag as Group
                        </button>
                        <button
                            className='tag-save-btn'
                            onClick={handleSave}
                            disabled={saving || !hasChanges}
                        >
                            {saving
                                ? 'Saving…'
                                : selectedArtists.length > 0
                                    ? `Update Tags — ${selectedArtists.length} Artist${selectedArtists.length !== 1 ? 's' : ''}`
                                    : 'Save (Untagged)'}
                        </button>
                    </div>
                </div>

                {selectedArtists.length > 0 && (
                    <div className='tag-name-group'>
                        <input
                            type='text'
                            className='tag-name-input'
                            placeholder='Save as named tag…'
                            value={tagName}
                            onChange={e => setTagName(e.target.value)}
                            onKeyDown={e => e.key === 'Enter' && handleSaveTag()}
                        />
                        <button
                            className='tag-save-tag-btn'
                            onClick={handleSaveTag}
                            disabled={savingTag || !tagName.trim()}
                        >
                            {savingTag ? 'Saving…' : 'Save Tag'}
                        </button>
                    </div>
                )}
            </div>
        </>
    );
}